/*
 * Starter-cart boost — cart layer.
 *
 * The offer is "$5 off at $50, if the cart started at $20 or less", and its
 * trigger is the CART, not the checkout window. This records the starting
 * subtotal in the `_starter_boost_start` cart attribute the first time we see
 * a non-empty cart. Everything else reads that figure back:
 * snippets/starter-boost.liquid to decide what the cart drawer shows, and —
 * once checkout opens — the starter-boost-banner extension, which copies it
 * into the app-reserved cart metafield the discount Function trusts. Recording
 * it HERE is what stops the drawer promising $5 that checkout then withholds
 * because by then the cart had grown past the starting maximum.
 *
 * The drawer has no product suggestions under the nudge (removed 2026-10-14);
 * the checkout banner's quick-add is separate and unaffected.
 *
 * Loaded from snippets/scripts.liquid only while the offer is enabled, and
 * self-contained (no @theme imports) so it is safe to load anywhere.
 *
 * The numbers all come from the shop metafield `birch.starter_boost_config`,
 * rendered into the markup by the snippet. Nothing here decides thresholds.
 */
(function () {
  'use strict';

  var ATTRIBUTE = '_starter_boost_start';

  /* One write in flight at a time. There is deliberately no "already
     recorded" latch: the attribute on the current cart is the record, so a
     new cart (checkout completed, cart cleared and attributes dropped) gets
     its own start instead of being skipped for the rest of the page view. */
  var recording = false;
  /* The latest full cart we have seen, so one cart change costs one
     /cart.js read. */
  var lastCart = null;

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /* ---------------------------------------------------------------- recording */

  /**
   * Write the starting subtotal, once per cart.
   *
   * Recorded for EVERY non-empty cart, not just eligible ones: a cart first
   * seen at $500 must stay recorded as $500, or a shopper could empty it down
   * to $15 and have it read as a small starter cart.
   *
   * `original_total_price` is the subtotal BEFORE any discount — the only
   * measure the discount Function can see, so the only one the thresholds can
   * be expressed in.
   */
  function startToRecord(cart) {
    if (!cart || !cart.item_count) return null;
    var existing = cart.attributes ? String(cart.attributes[ATTRIBUTE] || '').trim() : '';
    if (existing !== '') return null;
    var cents = Number(cart.original_total_price);
    if (!isFinite(cents) || cents < 0) return null;
    return String(Math.round(cents));
  }

  function recordStart(cart, fresh) {
    /* `fresh`: `cart` was read from /cart.js just now, not handed over by an event. */
    if (recording) return Promise.resolve();
    /* Already recorded (by us, an earlier page view, or the AI companion's
       own cart writes) — never overwrite it. */
    if (startToRecord(cart) === null) return Promise.resolve();
    recording = true;
    /* A cart handed over by an event can predate our own write; read the
       server's copy before writing so a recorded start is never replaced. */
    var current = fresh
      ? Promise.resolve(cart)
      : getJSON('/cart.js').then(function (latest) {
          if (isCart(latest)) lastCart = latest;
          return latest;
        });
    return current
      .then(function (latest) {
        var value = startToRecord(latest);
        if (value === null) return;
        var attributes = {};
        attributes[ATTRIBUTE] = value;
        /* Only the keys we send are touched, so other attributes (_registry_id,
           gift details) survive. No cart:update afterwards: nothing visible
           changed — the snippet already treated the current subtotal as the start. */
        return postJSON('/cart/update.js', { attributes: attributes });
      })
      .catch(function () {
        /* Unreachable → no record. Checkout still records its own, as before. */
      })
      .then(function () {
        recording = false;
      });
  }

  /* Pure helpers, exposed for unit/starter-boost.test.cjs the way the registry
     assets expose theirs. Not an API: nothing in the theme reads these. */
  window.BirchStarterBoost = { _startToRecord: startToRecord };

  /* -------------------------------------------------------------------- wiring */

  /* A full /cart.js-shaped cart — not the line item a CartAddEvent carries. */
  function isCart(cart) {
    return !!(
      cart &&
      Array.isArray(cart.items) &&
      typeof cart.item_count === 'number' &&
      cart.attributes &&
      cart.original_total_price !== undefined
    );
  }

  /* The cart as last seen, read once if we have none. */
  function currentCart() {
    if (lastCart) return Promise.resolve(lastCart);
    return getJSON('/cart.js').then(function (cart) {
      if (isCart(cart)) lastCart = cart;
      return cart;
    });
  }

  /* `resource` is the event's cart when it carries one (CartUpdateEvent from
     the cart components, the AI companion) — no second read then. */
  function sync(resource) {
    var fromEvent = isCart(resource);
    lastCart = fromEvent ? resource : null;
    return currentCart().then(function (cart) {
      /* Read just now → already the server's copy; from an event → re-read before writing. */
      return recordStart(cart, !fromEvent);
    });
  }

  sync();

  /* Any cart change can create the cart we have never recorded. */
  document.addEventListener('cart:update', function (event) {
    sync(event && event.detail ? event.detail.resource : null);
  });
})();
